import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    HStack,
    Box,
    Radio,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * RadioSortingOption is a React functional component that represents a sorting option
 * in the form of a radio button.
 *
 * @param {Object} props - The properties object.
 * @param {number} props.sortingIdx - The index of the sorting option, used for unique identification.
 * @param {string} props.sortingLabel - The label for the sorting option, displayed to the user.
 * @param {string} props.sortingHref - The URL to navigate to when the sorting option is selected.
 * @param {boolean} [props.isSelected=false] - A flag indicating whether this sorting option is currently selected.
 * @param {function} props.onSelect - A callback function to be called when the sorting option is selected.
 *
 * @returns {JSX.Element} A JSX element representing a sorting option with a radio button and label.
 */
const RadioSortingOption = ({
    sortingIdx,
    sortingLabel,
    isSelected = false,
    sortingId,
    onSelect
}) => {
    return (
        <HStack gap={0}>
            <Radio
                id={`radio-sorting-label-${sortingIdx}`}
                isChecked={isSelected}
                onChange={() => {
                    if (!isSelected) {
                        onSelect({
                            label: sortingLabel,
                            id: sortingId
                        })
                    }
                }}
            >
                {sortingLabel}
            </Radio>
        </HStack>
    )
}

RadioSortingOption.propTypes = {
    sortingIdx: PropTypes.number.isRequired,
    sortingLabel: PropTypes.string.isRequired,
    sortingHref: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    sortingId: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired
}

/**
 * RadioSortingOptions is a React functional component that renders a list of radio button options
 * for sorting items.
 *
 * This component is memoized using React.memo, meaning it will only re-render when its props change.
 * The props are used solely for data representation.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.sortUrls - An array of URLs corresponding to each sorting option. Each URL
 *     represents a link to the sorted list of products.
 * @param {Array} props.sortingOptions - An array of sorting option objects. Each object should
 *     contain an `id` and a `label` property, where `id` is a unique identifier for the sorting
 *     option and `label` is the display text for the option.
 * @param {Object} props.selectedSortingOptionLabel - The currently selected sorting option object.
 *     This object should contain a `label` property that matches one of the labels in the
 *     `sortingOptions` array, indicating the currently active sorting method.
 * @param {function} props.onSelectSorting - A callback function to be called when a sorting option is selected.
 *
 * @returns {JSX.Element|null} A JSX element containing a fieldset with a list of radio buttons for
 *     sorting options, or null if no sorting URLs are provided.
 */
const RadioSortingOptions = ({
    sortUrls = [],
    sortingOptions = [],
    selectedSortingOptionLabel,
    onSelectSorting
}) => {
    const {formatMessage} = useIntl()

    return (
        sortUrls.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage({
                        id: 'refinements.sorting.legend',
                        defaultMessage: 'Possible sorting options'
                    })}
                </legend>

                <List display="flex" flexDirection="column" gap={10}>
                    {sortUrls.map((href, idx) => (
                        <ListItem key={sortingOptions[idx]?.id} margin={0}>
                            <RadioSortingOption
                                sortingIdx={idx}
                                sortingLabel={sortingOptions[idx]?.label}
                                sortingId={sortingOptions[idx]?.id}
                                sortingHref={href}
                                isSelected={
                                    selectedSortingOptionLabel?.label === sortingOptions[idx]?.label
                                }
                                selectedSortingOptionLabel={selectedSortingOptionLabel?.label}
                                onSelect={onSelectSorting}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        )
    )
}

RadioSortingOptions.propTypes = {
    sortUrls: PropTypes.arrayOf(PropTypes.string),
    sortingOptions: PropTypes.arrayOf(PropTypes.object),
    selectedSortingOptionLabel: PropTypes.object,
    onSelectSorting: PropTypes.func
}

// Memoized RadioSortingOptions to prevent unnecessary re-renders.
// Use case: accordion open / close with RadioSortingOptions
export default React.memo(RadioSortingOptions)
